// Compliance Settings Page - Configure compliance regimes, intervals, and policies
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  ArrowLeft, 
  Info, 
  Save, 
  RotateCcw, 
  Plus, 
  Trash2,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  Settings2
} from 'lucide-react';
import { COMPLIANCE_REGIMES, type ComplianceRegimeCode } from '@/lib/compliance-data';

interface RegimeSettings {
  enabled: boolean;
  defaultInterval: number;
  intervalUnit: 'days' | 'months' | 'years';
  reminderDays: number[];
  requireCompetentPerson: boolean;
  requireEvidence: boolean;
  autoCreateWorkOrders: boolean;
  escalateOverdue: boolean;
  escalationDays: number;
}

interface LocationPolicy {
  locationId: string;
  locationName: string;
  regimeOverrides: Partial<Record<ComplianceRegimeCode, Partial<RegimeSettings>>>;
}

export default function ComplianceSettingsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('regimes');

  // Default settings for each regime
  const [regimeSettings, setRegimeSettings] = useState<Record<ComplianceRegimeCode, RegimeSettings>>({
    PUWER: {
      enabled: true,
      defaultInterval: 6,
      intervalUnit: 'months',
      reminderDays: [30, 14, 7],
      requireCompetentPerson: true,
      requireEvidence: true,
      autoCreateWorkOrders: true,
      escalateOverdue: true,
      escalationDays: 7
    },
    LOLER: {
      enabled: true,
      defaultInterval: 6,
      intervalUnit: 'months',
      reminderDays: [30, 14, 7],
      requireCompetentPerson: true,
      requireEvidence: true,
      autoCreateWorkOrders: true,
      escalateOverdue: true,
      escalationDays: 3
    },
    PAT: {
      enabled: true,
      defaultInterval: 12,
      intervalUnit: 'months',
      reminderDays: [30, 14],
      requireCompetentPerson: true,
      requireEvidence: true,
      autoCreateWorkOrders: false,
      escalateOverdue: false,
      escalationDays: 14
    },
    EICR: {
      enabled: true,
      defaultInterval: 5,
      intervalUnit: 'years',
      reminderDays: [90, 60, 30],
      requireCompetentPerson: true,
      requireEvidence: true,
      autoCreateWorkOrders: true,
      escalateOverdue: true,
      escalationDays: 30
    },
    MOT: {
      enabled: true,
      defaultInterval: 1,
      intervalUnit: 'years',
      reminderDays: [30, 14, 7],
      requireCompetentPerson: false,
      requireEvidence: true,
      autoCreateWorkOrders: false,
      escalateOverdue: true,
      escalationDays: 0
    },
    FIRE_EXTINGUISHER: {
      enabled: true,
      defaultInterval: 12,
      intervalUnit: 'months',
      reminderDays: [30, 14],
      requireCompetentPerson: true,
      requireEvidence: true,
      autoCreateWorkOrders: true,
      escalateOverdue: false,
      escalationDays: 14
    },
    CALIBRATION: {
      enabled: true,
      defaultInterval: 12,
      intervalUnit: 'months',
      reminderDays: [30, 14, 7],
      requireCompetentPerson: true,
      requireEvidence: true,
      autoCreateWorkOrders: true,
      escalateOverdue: true,
      escalationDays: 0
    },
    ISO_55001: {
      enabled: false,
      defaultInterval: 1,
      intervalUnit: 'years',
      reminderDays: [60, 30],
      requireCompetentPerson: false,
      requireEvidence: false,
      autoCreateWorkOrders: false,
      escalateOverdue: false,
      escalationDays: 30
    },
    ISO_45001: {
      enabled: false,
      defaultInterval: 1,
      intervalUnit: 'years',
      reminderDays: [60, 30],
      requireCompetentPerson: false,
      requireEvidence: false,
      autoCreateWorkOrders: false,
      escalateOverdue: false,
      escalationDays: 30
    }
  });

  const [locationPolicies, setLocationPolicies] = useState<LocationPolicy[]>([
    {
      locationId: 'LOC001',
      locationName: 'Main Depot',
      regimeOverrides: {
        PAT: { defaultInterval: 6, intervalUnit: 'months' }, // Higher risk environment
        PUWER: { defaultInterval: 3, intervalUnit: 'months' }
      }
    },
    {
      locationId: 'LOC002',
      locationName: 'Head Office',
      regimeOverrides: {
        PAT: { defaultInterval: 24, intervalUnit: 'months' } // Lower risk office
      }
    }
  ]);

  const [notificationSettings, setNotificationSettings] = useState({
    emailEnabled: true,
    emailRecipients: ['compliance@company.com', 'facilities@company.com'],
    dashboardNotifications: true,
    smsEnabled: false,
    smsRecipients: [] as string[]
  });

  const handleSave = () => {
    // TODO: Save to backend
    console.log('Saving compliance settings:', { regimeSettings, locationPolicies, notificationSettings });
    setHasChanges(false);
  };

  const handleReset = () => {
    // TODO: Reset to defaults
    setHasChanges(false);
  };

  const updateRegimeSetting = (regime: ComplianceRegimeCode, field: keyof RegimeSettings, value: any) => {
    setRegimeSettings(prev => ({
      ...prev,
      [regime]: {
        ...prev[regime],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Page Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Settings2 className="h-6 w-6 text-primary" />
              Compliance Settings
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Configure compliance regimes, intervals, and notification policies
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/compliance')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {hasChanges && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Unsaved Changes
              </Badge>
            )}
            <Button variant="outline" onClick={handleReset} disabled={!hasChanges}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="space-y-6 p-6">

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="regimes">
            <Settings2 className="h-4 w-4 mr-2" />
            Compliance Regimes
          </TabsTrigger>
          <TabsTrigger value="locations">Location Policies</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="iso">ISO Standards</TabsTrigger>
        </TabsList>

        {/* Compliance Regimes Tab */}
        <TabsContent value="regimes" className="space-y-4">
          {Object.entries(COMPLIANCE_REGIMES).map(([code, regime]) => {
            const settings = regimeSettings[code as ComplianceRegimeCode];
            return (
              <Card key={code} className="p-6">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{regime.name}</h3>
                        <Badge variant={settings.enabled ? 'default' : 'secondary'}>
                          {settings.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-md">
                              <div className="space-y-2">
                                <p className="font-semibold">{regime.regulatoryBody}</p>
                                <p className="text-sm">{regime.description}</p>
                                <p className="text-xs text-muted-foreground">
                                  Legislation: {regime.legislationReference}
                                </p>
                                <a 
                                  href={regime.guidanceUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  View Official Guidance <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{regime.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {regime.regulatoryBody} • {regime.legislationReference}
                      </p>
                    </div>
                    <Switch
                      checked={settings.enabled}
                      onCheckedChange={(checked) => updateRegimeSetting(code as ComplianceRegimeCode, 'enabled', checked)}
                    />
                  </div>

                  {settings.enabled && (
                    <>
                      {/* Default Interval */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Default Inspection Interval</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              min="1"
                              value={settings.defaultInterval}
                              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateRegimeSetting(
                                code as ComplianceRegimeCode, 
                                'defaultInterval', 
                                parseInt(e.target.value)
                              )}
                              className="w-24"
                            />
                            <Select
                              value={settings.intervalUnit}
                              onValueChange={(value: string) => updateRegimeSetting(
                                code as ComplianceRegimeCode, 
                                'intervalUnit', 
                                value
                              )}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="days">Days</SelectItem>
                                <SelectItem value="months">Months</SelectItem>
                                <SelectItem value="years">Years</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Recommended: {regime.defaultIntervals.join(', ')}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Reminder Days Before Due</Label>
                          <Input
                            value={settings.reminderDays.join(', ')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                              const days = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d));
                              updateRegimeSetting(code as ComplianceRegimeCode, 'reminderDays', days);
                            }}
                            placeholder="e.g., 30, 14, 7"
                          />
                          <p className="text-xs text-muted-foreground">
                            Comma-separated list of days
                          </p>
                        </div>
                      </div>

                      {/* Compliance Requirements */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="space-y-1">
                            <Label className="text-sm font-medium">Require Competent Person</Label>
                            <p className="text-xs text-muted-foreground">
                              Inspection must be performed by qualified personnel
                            </p>
                          </div>
                          <Switch
                            checked={settings.requireCompetentPerson}
                            onCheckedChange={(checked) => updateRegimeSetting(
                              code as ComplianceRegimeCode, 
                              'requireCompetentPerson', 
                              checked
                            )}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="space-y-1">
                            <Label className="text-sm font-medium">Require Evidence</Label>
                            <p className="text-xs text-muted-foreground">
                              Certificate or report must be uploaded
                            </p>
                          </div>
                          <Switch
                            checked={settings.requireEvidence}
                            onCheckedChange={(checked) => updateRegimeSetting(
                              code as ComplianceRegimeCode, 
                              'requireEvidence', 
                              checked
                            )}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="space-y-1">
                            <Label className="text-sm font-medium">Auto-Create Work Orders</Label>
                            <p className="text-xs text-muted-foreground">
                              Automatically generate work orders when due
                            </p>
                          </div>
                          <Switch
                            checked={settings.autoCreateWorkOrders}
                            onCheckedChange={(checked) => updateRegimeSetting(
                              code as ComplianceRegimeCode, 
                              'autoCreateWorkOrders', 
                              checked
                            )}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="space-y-1">
                            <Label className="text-sm font-medium">Escalate Overdue</Label>
                            <p className="text-xs text-muted-foreground">
                              Send escalation alerts to management
                            </p>
                          </div>
                          <Switch
                            checked={settings.escalateOverdue}
                            onCheckedChange={(checked) => updateRegimeSetting(
                              code as ComplianceRegimeCode, 
                              'escalateOverdue', 
                              checked
                            )}
                          />
                        </div>
                      </div>

                      {settings.escalateOverdue && (
                        <div className="space-y-2">
                          <Label>Escalation Delay (days after overdue)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={settings.escalationDays}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateRegimeSetting(
                              code as ComplianceRegimeCode, 
                              'escalationDays', 
                              parseInt(e.target.value)
                            )}
                            className="w-32"
                          />
                          <p className="text-xs text-muted-foreground">
                            0 = escalate immediately when overdue
                          </p>
                        </div>
                      )}

                      {/* Key Compliance Points */}
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Key Compliance Points</Label>
                        <ul className="space-y-1">
                          {regime.keyPoints.map((point, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </TabsContent>

        {/* Location Policies Tab */}
        <TabsContent value="locations" className="space-y-4">
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Location-Specific Policies</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Override default compliance intervals for specific sites or locations
                  </p>
                </div>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Location Policy
                </Button>
              </div>

              <div className="space-y-3">
                {locationPolicies.map((policy, idx) => (
                  <div key={policy.locationId} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium">{policy.locationName}</h4>
                        <p className="text-xs text-muted-foreground">ID: {policy.locationId}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Regime Overrides</Label>
                      {Object.entries(policy.regimeOverrides).map(([regime, overrides]) => (
                        <div key={regime} className="flex items-center gap-4 text-sm bg-muted/50 p-2 rounded">
                          <span className="font-medium min-w-[120px]">
                            {COMPLIANCE_REGIMES[regime as ComplianceRegimeCode].name}
                          </span>
                          <span className="text-muted-foreground">
                            Interval: {overrides.defaultInterval} {overrides.intervalUnit}
                          </span>
                        </div>
                      ))}
                      {Object.keys(policy.regimeOverrides).length === 0 && (
                        <p className="text-sm text-muted-foreground italic">No overrides configured</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Notification Settings</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure how and when compliance reminders are sent
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">Email Notifications</Label>
                    <p className="text-xs text-muted-foreground">
                      Send compliance reminders and alerts via email
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.emailEnabled}
                    onCheckedChange={(checked) => {
                      setNotificationSettings(prev => ({ ...prev, emailEnabled: checked }));
                      setHasChanges(true);
                    }}
                  />
                </div>

                {notificationSettings.emailEnabled && (
                  <div className="space-y-2 pl-4">
                    <Label>Email Recipients</Label>
                    {notificationSettings.emailRecipients.map((email, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input value={email} readOnly className="flex-1" />
                        <Button variant="ghost" size="sm">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Recipient
                    </Button>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">Dashboard Notifications</Label>
                    <p className="text-xs text-muted-foreground">
                      Show compliance alerts in the application dashboard
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.dashboardNotifications}
                    onCheckedChange={(checked) => {
                      setNotificationSettings(prev => ({ ...prev, dashboardNotifications: checked }));
                      setHasChanges(true);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">SMS Notifications</Label>
                    <p className="text-xs text-muted-foreground">
                      Send critical compliance alerts via SMS (additional charges may apply)
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.smsEnabled}
                    onCheckedChange={(checked) => {
                      setNotificationSettings(prev => ({ ...prev, smsEnabled: checked }));
                      setHasChanges(true);
                    }}
                  />
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ISO Standards Tab */}
        <TabsContent value="iso" className="space-y-4">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">ISO Standards Alignment</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure ISO 55001 (Asset Management) and ISO 45001 (OH&S) compliance tracking
                </p>
              </div>

              {/* ISO 55001 */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h4 className="font-medium">ISO 55001:2024 Asset Management</h4>
                  <Badge variant={regimeSettings.ISO_55001.enabled ? 'default' : 'secondary'}>
                    {regimeSettings.ISO_55001.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Track asset lifecycle management, risk-based decision making, and performance monitoring
                </p>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Readiness Checklist</Label>
                  <ul className="space-y-2">
                    {[
                      'Asset information accuracy and completeness',
                      'Lifecycle management plans documented',
                      'Risk assessment and cost-benefit analysis',
                      'Performance monitoring and KPIs defined',
                      'Decision criteria and approval workflows',
                      'Stakeholder engagement and communication'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="rounded" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <h4 className="font-medium">ISO 45001:2018 Occupational Health & Safety</h4>
                  <Badge variant={regimeSettings.ISO_45001.enabled ? 'default' : 'secondary'}>
                    {regimeSettings.ISO_45001.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Map compliance activities to OH&S management system (Plan-Do-Check-Act)
                </p>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Control Mapping</Label>
                  <ul className="space-y-2">
                    {[
                      'Hazard identification and risk assessment',
                      'Inspection and maintenance schedules',
                      'Competent person training records',
                      'Incident reporting and investigation',
                      'Performance monitoring and measurement',
                      'Management review and continual improvement'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="rounded" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
        </div>
      </div>
    </div>
  );
}
